import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import SalesSidebar from './SalesSidebar';
import SalesTopBar from './SalesTopBar';

const contentIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pop = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Root = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #f1f5f9; /* slate-50 */
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: ${(p) => (p.$mobile ? '0' : p.$sidebarOpen ? '256px' : '80px')};
  min-width: 0;
`;

const Content = styled.main`
  flex: 1;
  overflow-y: auto;
  background:
    radial-gradient(1000px 360px at 100% 0%, rgba(167, 139, 250, 0.14), transparent 70%),
    radial-gradient(900px 320px at 0% 100%, rgba(56, 189, 248, 0.12), transparent 70%),
    #f1f5f9;
  padding: ${(p) => (p.$mobile ? '16px' : '24px')};
  /* "backwards" (not "both"): a fill-forwards transform would stay on this scroll
     container and become the containing block for every position:fixed dialog
     inside it, pinning modals to the top of the scrolled content. */
  animation: ${contentIn} 0.45s ease backwards;
  scroll-behavior: smooth;

  /* ---- Shared polish for every Sales page (cards, tables, forms, dialogs) ---- */

  /* Opt-in staggered entrance: add className="sales-stagger" to a page's root element */
  .sales-stagger > * {
    animation: ${rise} 0.6s cubic-bezier(0.22, 1, 0.36, 1) backwards;
  }
  .sales-stagger > *:nth-child(1) { animation-delay: 0ms; }
  .sales-stagger > *:nth-child(2) { animation-delay: 80ms; }
  .sales-stagger > *:nth-child(3) { animation-delay: 160ms; }
  .sales-stagger > *:nth-child(4) { animation-delay: 240ms; }
  .sales-stagger > *:nth-child(5) { animation-delay: 320ms; }
  .sales-stagger > *:nth-child(6) { animation-delay: 400ms; }
  .sales-stagger > *:nth-child(n + 7) { animation-delay: 480ms; }

  /* Cards: soft lift on hover */
  [class*='rounded-xl'][class*='bg-white'],
  [class*='rounded-2xl'][class*='bg-white'] {
    transition: box-shadow 0.25s ease, border-color 0.25s ease;
  }
  section[class*='bg-white']:hover {
    border-color: #c7d2fe;
    box-shadow: 0 14px 34px -14px rgba(79, 70, 229, 0.25);
  }

  /* Tables */
  table thead th {
    letter-spacing: 0.04em;
    background-image: linear-gradient(180deg, #f8fafc, #f1f5f9);
  }
  table tbody tr {
    transition: background-color 0.15s ease;
  }
  table tbody tr:hover {
    background-color: rgba(238, 242, 255, 0.7);
  }

  /* Form fields: one consistent focus state */
  input:not([type='checkbox']):not([type='radio']):focus,
  select:focus,
  textarea:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
  }
  input, select, textarea {
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
  }

  /* Buttons: tactile feedback; primary buttons pick up the brand gradient */
  button, a[class*='rounded'] {
    transition: transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease, filter 0.2s ease;
  }
  button:not(:disabled):active {
    transform: scale(0.97);
  }
  button.bg-blue-600, button.bg-sky-600, button.bg-indigo-600, button.bg-sky-500, button.bg-blue-500 {
    background-image: linear-gradient(135deg, #6366f1, #8b5cf6);
    box-shadow: 0 8px 18px -6px rgba(99, 102, 241, 0.55);
  }
  button.bg-blue-600:not(:disabled):hover,
  button.bg-sky-600:not(:disabled):hover,
  button.bg-indigo-600:not(:disabled):hover,
  button.bg-sky-500:not(:disabled):hover,
  button.bg-blue-500:not(:disabled):hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }

  /* Dialogs: fade the backdrop, pop the panel */
  .fixed.inset-0 {
    animation: ${fadeIn} 0.2s ease both;
  }
  .fixed.inset-0 > div {
    animation: ${pop} 0.28s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 9999px; border: 2px solid transparent; background-clip: padding-box; }
  &::-webkit-scrollbar-thumb:hover { background: #94a3b8; background-clip: padding-box; border: 2px solid transparent; }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    scroll-behavior: auto;
    .sales-stagger > *, .fixed.inset-0, .fixed.inset-0 > div { animation: none; }
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 49;
  background: rgba(15, 23, 42, 0.5);
  animation: ${fadeIn} 0.2s ease both;
`;

const MOBILE_QUERY = '(max-width: 767px)';

export default function SalesLayout({ children, title, currentSection }) {
  const getIsMobile = () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches;
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (event) => {
      setIsMobile(event.matches);
      setDrawerOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!isMobile || !drawerOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobile, drawerOpen]);

  const handleMenuClick = () => {
    if (isMobile) setDrawerOpen((open) => !open);
    else setSidebarOpen((open) => !open);
  };

  return (
    <Root>
      <SalesSidebar
        isOpen={isMobile ? true : sidebarOpen}
        mobile={isMobile}
        drawerOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        currentSection={currentSection}
      />
      {isMobile && drawerOpen && <Backdrop onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      <Main $sidebarOpen={sidebarOpen} $mobile={isMobile}>
        <SalesTopBar title={title} onMenuClick={handleMenuClick} />
        <Content $mobile={isMobile}>{children}</Content>
      </Main>
    </Root>
  );
}
